import * as XLSX from 'xlsx';
import { DateTime } from 'luxon';

import { getName } from '../clock/ManualEntry/manualEntryHelpers';
import { normalizeCostcodeId } from '../forms/FormBuilder/FormFields/TimeTrackingGrid/TimeTrackingGridFromTasks';
import { sortByObjProperty } from '../common/helpers';

const HEADERS = [
  'Title',
  'Schedule Type',
  'Draft?',
  'Division',
  'Start Date/Time',
  'End Date/Time',
  'Timezone',
  'Project',
  'Phase',
  'Costcode',
  'Color',
  'Team',
  'Assigned Users',
  'Description',
];

const formatDateTime = (timestamp, timezone) => {
  if (!timestamp) return '';
  return DateTime.fromMillis(timestamp, { zone: timezone || undefined })
    .toLocaleString(DateTime.DATETIME_MED);
};

const joinNames = (ids = [], idMap = {}) => (
  ids
    .map((id) => getName(id, idMap))
    .filter(Boolean)
    .join(', ')
);

/**
 * Flattens a day-keyed shift map or array and dedupes by shift.id.
 * @param {Object|Array} shifts
 * @returns {Array}
 */
export const flattenAndDedupeShifts = (shifts) => {
  if (!shifts) return [];
  const list = Array.isArray(shifts) ? shifts : Object.values(shifts).flat();
  const seen = new Set();
  return list.filter((shift) => {
    if (!shift?.id || seen.has(shift.id)) return false;
    seen.add(shift.id);
    return true;
  });
};

/**
 * Keeps shifts that overlap [startDate, endDate] (inclusive millis).
 * When either bound is missing, returns shifts unchanged.
 */
export const filterShiftsByDateRange = (shifts, startDate, endDate) => {
  const list = shifts ?? [];
  if (startDate == null || endDate == null) return list;
  return list.filter(({ startTime = 0, endTime = 0 }) => (
    startTime <= endDate && endTime >= startDate
  ));
};

const buildRow = (shift, {
  userMap,
  projectMap,
  phaseMap,
  costcodeIdMap,
  divisionMap,
  teamMap,
}) => {
  const {
    title = '',
    isEvent,
    isDraft,
    divisionId,
    startTime,
    endTime,
    timezone = '',
    projectId,
    phaseId,
    costcodeId,
    color = '',
    teams = [],
    users = [],
    description = '',
  } = shift;

  const scheduleType = isEvent ? 'Event' : 'Shift';
  const draft = isDraft ? 'Yes' : 'No';
  const resolvedCostcodeId = normalizeCostcodeId(costcodeId);

  return [
    title,
    scheduleType,
    scheduleType === 'Shift' ? draft : '',
    getName(divisionId, divisionMap) ?? '',
    formatDateTime(startTime, timezone),
    formatDateTime(endTime, timezone),
    timezone,
    getName(projectId, projectMap) ?? '',
    getName(phaseId, phaseMap) ?? '',
    getName(resolvedCostcodeId, costcodeIdMap) ?? '',
    color,
    joinNames(teams, teamMap),
    joinNames(users, userMap),
    description ?? '',
  ];
};

/**
 * Exports filtered field-schedule shifts to an Excel file.
 * One row per unique shift; sorted by startTime ascending.
 * @param {Object|Array} shifts - day-keyed map or array of shifts
 * @param {DateTime} startDate - view range start
 * @param {DateTime} endDate - view range end
 * @param {Object} [userMap]
 * @param {Object} [projectMap]
 * @param {Object} [phaseMap]
 * @param {Object} [costcodeIdMap]
 * @param {Object} [divisionMap]
 * @param {Object} [teamMap]
 */
const exportFieldScheduleToExcel = ({
  shifts,
  startDate,
  endDate,
  userMap = {},
  projectMap = {},
  phaseMap = {},
  costcodeIdMap = {},
  divisionMap = {},
  teamMap = {},
}) => {
  const deduped = flattenAndDedupeShifts(shifts);
  const inRange = filterShiftsByDateRange(
    deduped,
    startDate.toMillis(),
    endDate.toMillis(),
  );
  inRange.sort(sortByObjProperty('startTime'));

  const lookupMaps = {
    userMap,
    projectMap,
    phaseMap,
    costcodeIdMap,
    divisionMap,
    teamMap,
  };

  const rows = [
    HEADERS,
    ...inRange.map((shift) => buildRow(shift, lookupMaps)),
  ];

  const startLabel = startDate.toLocaleString(DateTime.DATE_MED);
  const endLabel = endDate.toLocaleString(DateTime.DATE_MED);
  const title = `Field Schedule ${startLabel} - ${endLabel}`;

  const workbook = XLSX.utils.book_new();
  const sheet = XLSX.utils.aoa_to_sheet(rows);
  XLSX.utils.book_append_sheet(workbook, sheet, title.slice(0, 31));
  XLSX.writeFile(workbook, `${title}.xlsx`);
};

export default exportFieldScheduleToExcel;
